/**
 * Accept one shortage-report line's Helix/ERP difference as correct.
 *
 * The acceptance only holds while the figures shown here stay the same —
 * any change to Helix's status or balance, or to the ERP's balance, re-opens
 * the line — so the dialog shows them and asks for a reason.
 */
import { useEffect, useState } from 'react';
import type { ErpShortageRow } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Loader2 } from 'lucide-react';

interface AcceptShortageDifferenceDialogProps {
  /** Line to accept; null keeps the dialog closed. */
  row: ErpShortageRow | null;
  saving: boolean;
  onClose: () => void;
  onAccept: (reason: string) => void;
}

const MAX_REASON = 1000;

export default function AcceptShortageDifferenceDialog({ row, saving, onClose, onAccept }: AcceptShortageDifferenceDialogProps) {
  const { formatNumber } = useFormatters();
  const [reason, setReason] = useState('');

  useEffect(() => { setReason(''); }, [row?.schedule_id]);

  const trimmed = reason.trim();

  return (
    <Dialog open={row != null} onOpenChange={(open) => { if (!open && !saving) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Accept difference</DialogTitle>
          <DialogDescription>
            {row ? `${row.po_number}/${row.line_number}, schedule line ${row.schedule_line_number ?? '—'}` : ''}
          </DialogDescription>
        </DialogHeader>
        {row && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div className="rounded-md border bg-slate-50 p-2">
                <div className="text-xs text-muted-foreground">ERP balance</div>
                <div className="text-lg font-semibold tabular-nums">{row.erp_balance_qty != null ? formatNumber(row.erp_balance_qty) : '—'}</div>
              </div>
              <div className="rounded-md border p-2">
                <div className="text-xs text-muted-foreground">Helix balance</div>
                <div className="text-lg font-semibold tabular-nums">{formatNumber(row.balance_qty)}</div>
              </div>
              <div className="rounded-md border p-2">
                <div className="text-xs text-muted-foreground">Helix status</div>
                <div className="text-lg font-semibold capitalize">{row.status.replace(/_/g, ' ')}</div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              The line stops counting as needing repair while these figures stay the same. If Helix&apos;s status or
              balance or the ERP&apos;s balance changes, it needs repair again.
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="accept-difference-reason">Why is this difference correct?</Label>
              <Textarea
                id="accept-difference-reason"
                value={reason}
                maxLength={MAX_REASON}
                rows={3}
                placeholder="e.g. Receipt returned to supplier; ERP will not be updated"
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={() => onAccept(trimmed)} disabled={saving || !trimmed}>
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Accept difference
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
